import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { CameraService } from 'src/app/core/service/camera.service';
import { FaceDetectionService, FACE_CAPTURE_THRESHOLDS, FaceDetectionState, EMPTY_FACE_DETECTION } from 'src/app/core/service/face-detection.service';
import { BioDocumentValidationService, BioDocumentValidationResult } from '../service/bio-document-validation.service';
import { BioProcessImageStep, BioProcessInitResponse, BioSelfieCapture } from '../model/bio-process.model';
import { environment } from 'src/environments/environment';

export interface BioFaceCaptureResult {
  file: File;
  preview: string;
}

type BioFaceScreen =
  | 'FACE_READY'
  | 'FACE_CAPTURE'
  | 'VALIDATING'
  | 'SUCCESS'
  | 'RETRY_ERROR'
  | 'FINAL_ERROR'
  | 'CAMERA_ERROR'
  | 'BIO_TECHNICAL_ERROR'
  | 'PROCESS_EXPIRED'
  | 'PRESIGNED_EXPIRED';

type FaceChallenge =
  | 'LOOK_FORWARD'
  | 'LOOK_LEFT'
  | 'LOOK_RIGHT'
  | 'LOOK_UP'
  | 'LOOK_DOWN'
  | 'TILT_LEFT'
  | 'TILT_RIGHT'
  | 'OPEN_MOUTH'
  | 'SMILE'
  | 'CLOSE_EYES'
  | 'WINK'
  | 'MOVE_CLOSER'
  | 'FACE_CENTERED'
  | 'HOLD_STILL';

@Component({
  selector: 'app-bio-face-flow',
  templateUrl: './bio-face-flow.component.html',
  styleUrls: ['./bio-face-flow.component.scss'],
})
export class BioFaceFlowComponent implements AfterViewInit, OnDestroy {
  /** Comparte el requestId LIVEPROBE iniciado al validar el DNI. */
  @Input() initResponse?: BioProcessInitResponse;
  @Output() completed = new EventEmitter<BioFaceCaptureResult>();
  @Output() validationResult = new EventEmitter<BioDocumentValidationResult>();
  @Output() exit = new EventEmitter<void>();
  @Output() finalError = new EventEmitter<void>();

  @ViewChild('video', { static: false }) videoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas', { static: false }) canvasRef?: ElementRef<HTMLCanvasElement>;

  screen: BioFaceScreen = 'FACE_READY';
  cameraError = '';
  isStarting = false;
  isCapturing = false;
  countdown: number | null = null;
  feedback = 'Ubica tu cara dentro del óvalo';
  baseFeedback: string | null = null;
  detection: FaceDetectionState = EMPTY_FACE_DETECTION;
  previewUrl: string | null = null;
  bioError = '';
  bioErrorTitle = 'No pudimos completar la validación';
  failedValidation = false;
  selfieInstructions: BioProcessImageStep[] = [];
  selfieCaptures: BioSelfieCapture[] = [];
  activeSelfieIndex = 0;
  private liveprobeInitResponse: BioProcessInitResponse | null = null;
  private detectionFrameId: number | null = null;
  private mediaStream: MediaStream | null = null;
  private cameraReady = false;
  private lastDetectionAt = 0;
  private lastPoseDebugAt = 0;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  private stableSince: number | null = null;
  private stableFace: { centerX: number; centerY: number; width: number } | null = null;
  private initialPitch: number | null = null;
  private neutralPitchTotal = 0;
  private neutralPitchFrames = 0;
  private lookUpValidFrames = 0;
  private transactionId: string | null = null;
  private processExpired = false;

  constructor(
    private readonly cameraService: CameraService,
    private readonly faceDetection: FaceDetectionService,
    private readonly validationService: BioDocumentValidationService,
    private readonly cd: ChangeDetectorRef,
  ) {}

  ngAfterViewInit(): void {
    if (environment.type === 'DEV') {
      console.info('[BIO TRACE] face component mounted');
    }
    this.initializeLiveprobe();
  }

  ngOnDestroy(): void {
    this.stopCamera();
  }

  goBack(): void {
    this.resetFlow();
    this.exit.emit();
  }

  resetFlow(): void {
    this.stopCamera();
    this.isStarting = false;
    this.previewUrl = null;
    this.detection = EMPTY_FACE_DETECTION;
    this.resetChallengeState();
    this.cameraError = '';
    this.cameraReady = false;
    this.transactionId = null;
    this.bioError = '';
    this.bioErrorTitle = 'No pudimos completar la validación';
    this.failedValidation = false;
    this.processExpired = false;
    this.baseFeedback = null;
    this.selfieInstructions = [];
    this.selfieCaptures = [];
    this.activeSelfieIndex = 0;
    this.liveprobeInitResponse = null;
    this.screen = 'FACE_READY';
  }

  get activeSelfieInstruction(): BioProcessImageStep | null {
    return this.selfieInstructions[this.activeSelfieIndex] ?? null;
  }

  get faceTitle(): string {
    if (this.screen === 'FACE_READY') {
      return 'Ubicá tu cara en el óvalo y continuá';
    }
    return this.activeSelfieInstruction?.label ?? this.feedback;
  }

  get facePositionFeedback(): string {
    if (this.countdown !== null || this.isCapturing) {
      return 'No te muevas';
    }
    const position = this.resolveBaseFaceFeedback(this.detection);
    if (position === 'Acercá tu cara') {
      return 'Acercate un poco';
    }
    if (position === 'Alejá tu cara') {
      return 'Alejate un poco';
    }
    return position ? 'Ubicá tu cara dentro del óvalo' : 'Perfecto, no te muevas';
  }

  get isFaceBaseValid(): boolean {
    return this.resolveBaseFaceFeedback(this.detection) === null;
  }

  get isFaceStable(): boolean {
    return this.screen === 'FACE_CAPTURE' && this.isFaceBaseValid &&
      this.stableSince !== null && Date.now() - this.stableSince >= FACE_CAPTURE_THRESHOLDS.stabilityMs;
  }

  async prepareCamera(): Promise<void> {
    if (this.isStarting || this.cameraReady || this.screen !== 'FACE_READY') {
      return;
    }

    this.isStarting = true;
    this.cameraError = '';
    this.cd.detectChanges();

    try {
      if (!this.cameraService.isSupported()) {
        throw { code: 'NOT_SUPPORTED', message: 'Tu navegador no admite la cámara.' };
      }

      this.mediaStream = await this.cameraService.requestCamera('user');
      this.cd.detectChanges();
      const video = this.videoRef?.nativeElement;
      if (!video) {
        throw new Error('No se pudo preparar la vista de cámara.');
      }

      video.srcObject = this.mediaStream;
      await this.waitForVideoMetadata(video);
      await video.play();
      if (video.videoWidth <= 0 || video.videoHeight <= 0) {
        throw new Error('No se pudo iniciar la cámara.');
      }

      this.cameraReady = true;
      this.cd.detectChanges();
    } catch (error) {
      const message = (error as { message?: string })?.message || 'No se pudo acceder a la cámara.';
      this.cameraError = message;
      this.stopCamera();
      this.screen = 'CAMERA_ERROR';
    } finally {
      this.isStarting = false;
      this.cd.detectChanges();
    }
  }

  async beginDetection(): Promise<void> {
    if (this.screen !== 'FACE_READY') {
      return;
    }

    if (!this.cameraReady) {
      await this.prepareCamera();
      if (!this.cameraReady) {
        return;
      }
    }

    if (!this.activeSelfieInstruction) {
      this.showBioTechnicalError('El proceso facial incluye un desafío no compatible.');
      return;
    }

    try {
      await this.faceDetection.initialize();
      this.screen = 'FACE_CAPTURE';
      this.cd.detectChanges();
      this.startDetectionLoop();
    } catch {
      this.showBioTechnicalError('No se pudo iniciar la detección facial. Intentá nuevamente.');
    }
  }

  retryCamera(): void {
    this.stopCamera();
    this.cameraError = '';
    this.cameraReady = false;
    this.screen = 'FACE_READY';
    this.cd.detectChanges();
    void this.prepareCamera();
  }

  retryBio(): void {
    if (this.screen === 'RETRY_ERROR' && this.liveprobeInitResponse) {
      this.validationService.clearPresignedCache(
        this.liveprobeInitResponse.requestId,
        this.selfieCaptures.map((capture) => capture.imageType),
      );
    }
    this.resetFlow();
    this.initializeLiveprobe();
  }

  closeFailedSelfie(): void {
    this.stopCamera();
    this.faceDetection.close();
    this.acceptFinalError();
  }

  showFinalError(): void {
    this.stopCamera();
    this.screen = 'FINAL_ERROR';
  }

  acceptFinalError(): void {
    this.stopCamera();
    if (this.processExpired) {
      this.faceDetection.close();
    }
    this.finalError.emit();
  }

  showSuccess(): void {
    this.stopCamera();
    this.screen = 'SUCCESS';
  }

  private waitForVideoMetadata(video: HTMLVideoElement): Promise<void> {
    if (video.readyState >= HTMLMediaElement.HAVE_METADATA && video.videoWidth > 0 && video.videoHeight > 0) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const onLoadedMetadata = (): void => {
        cleanup();
        resolve();
      };
      const onError = (): void => {
        cleanup();
        reject(new Error('No se pudo iniciar la cámara.'));
      };
      const cleanup = (): void => {
        video.removeEventListener('loadedmetadata', onLoadedMetadata);
        video.removeEventListener('error', onError);
      };
      video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true });
      video.addEventListener('error', onError, { once: true });
    });
  }

  private startDetectionLoop(): void {
    this.stopDetectionLoop();
    this.lastDetectionAt = 0;
    this.scheduleNextFrame();
  }

  private scheduleNextFrame(): void {
    if (this.detectionFrameId === null && this.screen === 'FACE_CAPTURE') {
      this.detectionFrameId = requestAnimationFrame(this.detectFrame);
    }
  }

  private readonly detectFrame = (timestamp: number): void => {
    this.detectionFrameId = null;
    const video = this.videoRef?.nativeElement;
    if (!this.canDetect(video)) {
      this.scheduleNextFrame();
      return;
    }

    if (timestamp - this.lastDetectionAt >= FACE_CAPTURE_THRESHOLDS.detectionIntervalMs) {
      this.lastDetectionAt = timestamp;
      try {
        const state = this.faceDetection.detect(video!, timestamp);
        this.detection = state;
        this.updateChallengeState(state);
      } catch {
        this.showBioTechnicalError('No se pudo seguir la detección facial. Intentá nuevamente.');
        return;
      }
    }

    this.scheduleNextFrame();
  };

  private canDetect(video: HTMLVideoElement | undefined): boolean {
    const track = this.mediaStream?.getVideoTracks()[0];
    return !!video &&
      this.screen === 'FACE_CAPTURE' &&
      video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
      video.videoWidth > 0 &&
      video.videoHeight > 0 &&
      !!track &&
      track.readyState === 'live';
  }

  private stopDetectionLoop(): void {
    if (this.detectionFrameId !== null) {
      cancelAnimationFrame(this.detectionFrameId);
      this.detectionFrameId = null;
    }
  }

  private stopCamera(): void {
    this.stopDetectionLoop();
    this.stopChallengeCountdown();
    this.mediaStream = null;
    this.cameraReady = false;
    this.cameraService.stopCamera();
  }

  private updateChallengeState(state: FaceDetectionState): void {
    const baseFeedback = this.resolveBaseFaceFeedback(state);
    if (baseFeedback) {
      this.baseFeedback = baseFeedback;
      this.stopChallengeCountdown();
      return;
    }

    this.baseFeedback = null;
    const centerX = (state.faceLeft + state.faceRight) / 2;
    const centerY = (state.faceTop + state.faceBottom) / 2;
    if (this.stableFace && (
      Math.abs(centerX - this.stableFace.centerX) > 0.04 ||
      Math.abs(centerY - this.stableFace.centerY) > 0.04 ||
      Math.abs(state.faceWidthRatio - this.stableFace.width) > 0.05
    )) {
      this.stopChallengeCountdown();
    }
    if (!this.stableFace) {
      this.stableFace = { centerX, centerY, width: state.faceWidthRatio };
    }
    this.feedback = this.activeSelfieInstruction?.label || 'Mantené la posición';
    if (this.countdownTimer !== null || this.isCapturing) {
      return;
    }
    if (this.stableSince === null) {
      this.stableSince = Date.now();
      return;
    }

    if (Date.now() - this.stableSince >= FACE_CAPTURE_THRESHOLDS.stabilityMs) {
      this.startCountdown();
    }
  }

  private resolveBaseFaceFeedback(state: FaceDetectionState): string | null {
    if (!state.detected) return 'Ubicá tu cara en el óvalo';
    if (state.multipleFaces) return 'Asegurate de que haya una sola cara';
    if (!state.faceWithinSafeOval) return 'Ubicá tu cara en el óvalo';
    if (!state.centered) return 'Centrá tu rostro';
    if (!this.isFaceSizeValid(state)) return state.faceWidthRatio < FACE_CAPTURE_THRESHOLDS.minFaceSize ? 'Acercá tu cara' : 'Alejá tu cara';
    return null;
  }

  private isFaceSizeValid(state: FaceDetectionState): boolean {
    return state.faceWidthRatio >= FACE_CAPTURE_THRESHOLDS.minFaceSize &&
      state.faceWidthRatio <= FACE_CAPTURE_THRESHOLDS.maxFaceSize;
  }

  private isChallengeValid(challenge: FaceChallenge, state: FaceDetectionState): boolean {
    const isNeutral = Math.abs(state.pitch) <= FACE_CAPTURE_THRESHOLDS.pitchTolerance && Math.abs(state.roll) <= FACE_CAPTURE_THRESHOLDS.rollTolerance;
    const validators: Record<FaceChallenge, (face: FaceDetectionState) => boolean> = {
      LOOK_FORWARD: (face) => isNeutral && Math.abs(face.yaw) <= FACE_CAPTURE_THRESHOLDS.yawForward && Math.abs(face.gazeHorizontal) <= FACE_CAPTURE_THRESHOLDS.gazeHorizontalThreshold && Math.abs(face.gazeVertical) <= FACE_CAPTURE_THRESHOLDS.gazeVerticalThreshold,
      LOOK_LEFT: (face) => isNeutral && face.gazeHorizontal <= -FACE_CAPTURE_THRESHOLDS.gazeHorizontalThreshold,
      LOOK_RIGHT: (face) => isNeutral && face.gazeHorizontal >= FACE_CAPTURE_THRESHOLDS.gazeHorizontalThreshold,
      LOOK_UP: (face) => this.isLookUpValid(face),
      LOOK_DOWN: (face) => Math.abs(face.roll) <= FACE_CAPTURE_THRESHOLDS.rollTolerance && Math.abs(face.pitch) <= FACE_CAPTURE_THRESHOLDS.pitchTolerance && face.leftEyeGazeY >= FACE_CAPTURE_THRESHOLDS.gazeVerticalThreshold && face.rightEyeGazeY >= FACE_CAPTURE_THRESHOLDS.gazeVerticalThreshold,
      TILT_LEFT: (face) => Math.abs(face.pitch) <= FACE_CAPTURE_THRESHOLDS.pitchTolerance && face.roll <= -FACE_CAPTURE_THRESHOLDS.rollSide,
      TILT_RIGHT: (face) => Math.abs(face.pitch) <= FACE_CAPTURE_THRESHOLDS.pitchTolerance && face.roll >= FACE_CAPTURE_THRESHOLDS.rollSide,
      OPEN_MOUTH: (face) => isNeutral && face.mouthOpen,
      SMILE: (face) => isNeutral && face.smiling,
      CLOSE_EYES: (face) => isNeutral && face.leftEyeClosed && face.rightEyeClosed,
      WINK: (face) => isNeutral && face.leftEyeClosed !== face.rightEyeClosed,
      MOVE_CLOSER: (face) => isNeutral && face.faceWidthRatio >= FACE_CAPTURE_THRESHOLDS.closeFaceSize,
      FACE_CENTERED: (face) => isNeutral && face.faceWithinSafeOval,
      HOLD_STILL: (face) => isNeutral && Math.abs(face.yaw) <= FACE_CAPTURE_THRESHOLDS.yawForward,
    };
    return validators[challenge](state);
  }

  private isLookUpValid(state: FaceDetectionState): boolean {
    const pitchDelta = this.initialPitch !== null && state.headPitch3d !== undefined
      ? state.headPitch3d - this.initialPitch : null;
    const tiltedUp = pitchDelta !== null && Number.isFinite(pitchDelta) && pitchDelta <= -10 &&
      Math.abs(state.roll) <= FACE_CAPTURE_THRESHOLDS.rollTolerance;
    this.lookUpValidFrames = tiltedUp ? this.lookUpValidFrames + 1 : 0;
    return this.lookUpValidFrames >= 4;
  }

  private normalizeImageAction(imageType: string, label: string): string {
    const normalizedLabel = label.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    return imageType === 'SELFIE_OPEN_MOUTH' && /guin/.test(normalizedLabel) ? 'SELFIE_WINK' : imageType;
  }

  private logPoseDebug(challenge: FaceChallenge, state: FaceDetectionState, valid: boolean): void {
    const now = Date.now();
    if (environment.type !== 'DEV' || now - this.lastPoseDebugAt < (challenge === 'LOOK_UP' ? 200 : 500)) {
      return;
    }
    this.lastPoseDebugAt = now;
    if (challenge === 'LOOK_FORWARD' || challenge === 'LOOK_UP' || challenge === 'LOOK_DOWN') {
      console.debug('[SELFIE-POSE]', {
        pitch: state.headPitch3d === undefined ? null : Math.round(state.headPitch3d * 10) / 10,
        legacyPitch2d: Math.round(state.pitch * 10) / 10,
        initialPitch: this.initialPitch === null ? null : Math.round(this.initialPitch * 10) / 10,
        pitchDelta: this.initialPitch === null || state.headPitch3d === undefined ? null :
          Math.round((state.headPitch3d - this.initialPitch) * 10) / 10,
        yaw: Math.round(state.yaw * 10) / 10,
        roll: Math.round(state.roll * 10) / 10,
        expectedGesture: challenge,
        detectedGesture: valid ? challenge : null,
        consecutiveFrames: challenge === 'LOOK_UP' ? this.lookUpValidFrames : null,
        faceReady: this.resolveBaseFaceFeedback(state) === null,
      });
    }
    console.debug('[BIO FACE]', {
      challenge: this.activeSelfieInstruction?.image_type || challenge,
      normalizedChallenge: challenge,
      yaw: Math.round(state.yaw * 10) / 10,
      pitch: Math.round(state.pitch * 10) / 10,
      roll: Math.round(state.roll * 10) / 10,
      gazeHorizontal: Math.round(state.gazeHorizontal * 100) / 100,
      gazeVertical: Math.round(state.gazeVertical * 100) / 100,
      leftEyeGazeY: Math.round(state.leftEyeGazeY * 100) / 100,
      rightEyeGazeY: Math.round(state.rightEyeGazeY * 100) / 100,
      valid,
    });
  }

  private mapImageTypeToChallenge(imageType: string): FaceChallenge | null {
    const challengeByImageType: Record<string, FaceChallenge> = {
      SELFIE_LOOK_FORWARD: 'LOOK_FORWARD',
      SELFIE_LOOK_LEFT: 'LOOK_LEFT',
      SELFIE_LOOK_RIGHT: 'LOOK_RIGHT',
      SELFIE_LOOK_UP: 'LOOK_UP',
      SELFIE_LOOK_DOWN: 'LOOK_DOWN',
      SELFIE_TILT_LEFT: 'TILT_LEFT',
      SELFIE_TILT_RIGHT: 'TILT_RIGHT',
      SELFIE_OPEN_MOUTH: 'OPEN_MOUTH',
      SELFIE_SMILE: 'SMILE',
      SELFIE_CLOSE_EYES: 'CLOSE_EYES',
      SELFIE_WINK: 'WINK',
      SELFIE_MOVE_CLOSER: 'MOVE_CLOSER',
      SELFIE_FACE_CENTERED: 'FACE_CENTERED',
      SELFIE_HOLD_STILL: 'HOLD_STILL',
    };
    return challengeByImageType[imageType] || null;
  }

  private stopChallengeCountdown(): void {
    this.stopCountdown();
    this.stableSince = null;
    this.stableFace = null;
  }

  private resetChallengeState(): void {
    this.stopChallengeCountdown();
    this.initialPitch = null;
    this.neutralPitchTotal = 0;
    this.neutralPitchFrames = 0;
    this.lookUpValidFrames = 0;
    this.baseFeedback = null;
    this.feedback = 'Ubicá tu cara en el óvalo';
    this.isCapturing = false;
  }

  private startCountdown(): void {
    if (this.countdownTimer !== null || this.isCapturing) {
      return;
    }

    this.countdown = FACE_CAPTURE_THRESHOLDS.countdownSeconds;
    this.feedback = 'Preparando captura';
    this.countdownTimer = setInterval(() => {
      if (!this.isFaceBaseValid || !this.canDetect(this.videoRef?.nativeElement)) {
        this.stopChallengeCountdown();
        return;
      }
      if (this.countdown === null) {
        return;
      }
      if (this.countdown > 1) {
        this.countdown -= 1;
        return;
      }

      this.stopCountdown();
      void this.captureSelfie();
    }, 1000);
  }

  private stopCountdown(): void {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    this.countdown = null;
  }

  async captureSelfie(): Promise<void> {
    if (this.isCapturing) {
      return;
    }

    this.isCapturing = true;
    const video = this.videoRef?.nativeElement;
    const canvas = this.canvasRef?.nativeElement;
    if (!video || !canvas) {
      this.isCapturing = false;
      this.feedback = 'No se pudo capturar la imagen';
      return;
    }

    if (!this.canDetect(video)) {
      this.isCapturing = false;
      this.feedback = 'Esperá un momento para capturar la imagen';
      return;
    }

    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    const targetAspect = 4 / 3;
    const videoAspect = videoWidth / videoHeight;
    let sx = 0;
    let sy = 0;
    let sw = videoWidth;
    let sh = videoHeight;

    if (videoAspect > targetAspect) {
      sw = videoHeight * targetAspect;
      sx = (videoWidth - sw) / 2;
    } else {
      sh = videoWidth / targetAspect;
      sy = (videoHeight - sh) / 2;
    }

    canvas.width = sw;
    canvas.height = sh;
    const context = canvas.getContext('2d');
    if (!context) {
      this.isCapturing = false;
      return;
    }

    context.save();
    context.translate(sw, 0);
    context.scale(-1, 1);
    context.drawImage(video, sx, sy, sw, sh, 0, 0, sw, sh);
    context.restore();

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    const instruction = this.activeSelfieInstruction;
    if (!instruction) {
      this.isCapturing = false;
      this.showBioTechnicalError('No encontramos la instrucción de selfie requerida.');
      return;
    }
    const file = this.dataUrlToFile(dataUrl, `selfie-${instruction.image_id.toLowerCase()}.jpg`);
    this.previewUrl = dataUrl;
    this.isCapturing = false;
    this.selfieCaptures.push({
      imageId: instruction.image_id,
      imageType: instruction.image_type,
      label: instruction.label,
      file,
    });

    if (this.activeSelfieIndex + 1 < this.selfieInstructions.length) {
      this.activeSelfieIndex += 1;
      this.resetChallengeState();
      this.cd.detectChanges();
      return;
    }

    this.stopCamera();
    this.submitSelfieCaptures();
  }

  private initializeLiveprobe(): void {
    if (!this.transactionId) {
      this.transactionId = this.createTransactionId();
    }
    this.bioError = '';
    if (environment.type === 'DEV') {
      console.info('[BIO TRACE] LIVEPROBE init reused from DNI step (no second init-process)');
    }
    if (!this.initResponse) {
      this.showBioTechnicalError('No se pudo continuar con la verificación facial. Volvé a intentar desde el DNI.');
      return;
    }
    this.liveprobeInitResponse = this.initResponse;
    this.selfieInstructions = this.getSelfieInstructions(this.initResponse);
    this.selfieCaptures = [];
    this.activeSelfieIndex = 0;
    if (this.selfieInstructions.length === 0) {
      this.showBioTechnicalError('El proceso facial no incluyó desafíos requeridos.');
      return;
    }
    this.feedback = this.selfieInstructions[0].label || 'Ubicá tu cara en el óvalo y continuá';
    this.screen = 'FACE_READY';
    this.cd.detectChanges();
    void this.prepareCamera();
  }

  private submitSelfieCaptures(): void {
    if (!this.transactionId || !this.liveprobeInitResponse || this.selfieCaptures.length !== this.selfieInstructions.length) {
      this.showBioTechnicalError('Faltan selfies requeridas para continuar.');
      return;
    }
    this.screen = 'VALIDATING';
    this.validationService.validateSelfieCaptures(
      this.liveprobeInitResponse,
      this.selfieCaptures,
      this.transactionId,
    ).subscribe((result) => {
      this.validationResult.emit(result);
      if (result.success) {
        this.screen = 'SUCCESS';
        const finalCapture = this.selfieCaptures[this.selfieCaptures.length - 1];
        this.completed.emit({ file: finalCapture.file, preview: this.previewUrl || '' });
        return;
      }

      if (result.status === 'failed' || result.status === 'retry') {
        this.stopCamera();
        this.failedValidation = result.status === 'failed';
        this.bioErrorTitle = this.failedValidation ? 'No pudimos validar la selfie' : result.title || 'No pudimos completar la validación';
        this.bioError = this.failedValidation ? 'No pudimos validar la selfie. Intentá nuevamente.' : result.message || 'Intentá nuevamente.';
        this.screen = 'RETRY_ERROR';
        this.cd.detectChanges();
        return;
      }

      if (result.status === 'finish_fail') {
        this.showFinalError();
        return;
      }

      if (result.errorKind === 'PROCESS_EXPIRED') {
        this.showProcessExpired(result.errorPhase === 'PRESIGNED_URL');
        return;
      }

      if (result.errorKind === 'PRESIGNED_URL_ERROR' || result.errorPhase === 'PRESIGNED_URL') {
        this.showBioTechnicalError(result.message || 'Ocurrió un problema durante la validación. Intentá nuevamente.');
        return;
      }

      if (
        result.errorKind === 'UPLOAD_ERROR' ||
        result.errorKind === 'VALIDATE_PROCESS_ERROR' ||
        result.errorPhase === 'UPLOAD' ||
        result.errorPhase === 'VALIDATE_PROCESS'
      ) {
        this.showFinalError();
        return;
      }

      this.showBioTechnicalError(result.message || 'Ocurrió un problema durante la validación. Intentá nuevamente.');
    });
  }

  private restartFaceCapture(restartLiveprobe = false): void {
    this.previewUrl = null;
    this.detection = EMPTY_FACE_DETECTION;
    this.feedback = 'Ubicá tu cara en el óvalo y continuá';
    this.resetChallengeState();
    this.selfieCaptures = [];
    this.activeSelfieIndex = 0;
    this.liveprobeInitResponse = null;
    this.screen = 'FACE_READY';
    this.cd.detectChanges();
    if (restartLiveprobe) {
      this.initializeLiveprobe();
      return;
    }
    void this.prepareCamera();
  }

  private getSelfieInstructions(initResponse: BioProcessInitResponse): BioProcessImageStep[] {
    return initResponse.steps
      .filter((step) => step.step_name === 'VALIDATE_SELFIE')
      .flatMap((step) => step.images || []);
  }

  private showBioTechnicalError(message: string): void {
    this.stopCamera();
    this.bioError = message;
    this.screen = 'BIO_TECHNICAL_ERROR';
    this.cd.detectChanges();
  }

  private showProcessExpired(asModal = false): void {
    this.processExpired = true;
    this.stopCamera();
    this.bioErrorTitle = 'La verificación expiró';
    this.bioError = 'El tiempo para completar la validación biométrica finalizó. Volvé a iniciar el proceso.';
    this.screen = asModal ? 'PRESIGNED_EXPIRED' : 'PROCESS_EXPIRED';
    this.cd.detectChanges();
  }

  private createTransactionId(): string {
    if (typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  private dataUrlToFile(dataUrl: string, filename: string): File {
    const [matchHeader, base64] = dataUrl.split(',');
    const mime = matchHeader?.match(/:(.*?);/)?.[1] ?? 'image/jpeg';
    const binary = atob(base64 ?? '');
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return new File([bytes], filename, { type: mime });
  }
}
